import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { Observable } from 'rxjs';
import { map, share } from 'rxjs/operators';
import { CircleSearchResults, CitySearchResults } from 'src/app/shared/models/courts/circleSearchResults';
import { Circle } from 'src/app/shared/models/courts/circle';
import { Court } from 'src/app/shared/models/courts/court';
import { CourtSearchResults } from 'src/app/shared/models/courts/courtSearchResults';
import { SearchParams } from 'src/app/shared/models/searchParams';
import { environment } from 'src/environments/environment';
import { ErrorService } from '../error/error.service';
import { ApiBaseService } from './api-base.service';
import { City } from 'src/app/shared/models/courts/city';

@Injectable({
  providedIn: 'root'
})
export class CourtsApiService extends ApiBaseService {

  baseUrl = environment.backend_host + '/api/courts';

  constructor(
    public http: HttpClient,
    protected errorService: ErrorService,
    protected spinner: NgxSpinnerService) {
    super(spinner, errorService);
  }

  // all courts
  public getCourts = (params: SearchParams): Observable<CourtSearchResults> => {
    const p = this.serializeParamsObj(params, new HttpParams());
    return this.http.get<any>(`${this.baseUrl}/`, { params: p })
      .pipe(map(this.deserializeAs(CourtSearchResults)));
  }

  public getCourt = (id: number): Observable<Court> => {
    return this.http.get<any>(`${this.baseUrl}/${id}`)
      .pipe(map(this.deserializeAs(Court)));
  }

  public updateCourt = (court: Court): Observable<Court> => {
    return this.http.put<any>(`${this.baseUrl}/${court.id}`, this.serialize(court))
      .pipe(map(this.deserializeAs(Court)));
  }

  public createCourt = (court: Court): Observable<Court> => {
    return this.http.post<any>(`${this.baseUrl}/`, this.serialize(court))
      .pipe(map(this.deserializeAs(Court)));
  }

  public deleteCourt = (court: Court): Observable<Court> => {
    return this.http.delete<any>(`${this.baseUrl}/${court.id}`)
      .pipe(share());
  }

  // Circles
  public getCircles = (params: SearchParams): Observable<CircleSearchResults> => {
    const p = this.serializeParamsObj(params, new HttpParams());
    return this.http.get<any>(`${this.baseUrl}/circles`, { params: p })
      .pipe(map(this.deserializeAs(CircleSearchResults)));
  }

  public getCircle = (id: number): Observable<Circle> => {
    return this.http.get<any>(`${this.baseUrl}/circles/${id}`)
      .pipe(map(this.deserializeAs(Circle)));
  }

  public updateCircle = (circle: Circle): Observable<Circle> => {
    return this.http.put<any>(`${this.baseUrl}/circles/${circle.id}`, this.serialize(circle))
      .pipe(map(this.deserializeAs(Circle)));
  }

  public createCircle = (circle: Circle): Observable<Circle> => {
    return this.http.post<any>(`${this.baseUrl}/circles`, this.serialize(circle))
      .pipe(map(this.deserializeAs(Circle)));
  }

  public deleteCircle = (circle: Circle): Observable<Circle> => {
    return this.http.delete<any>(`${this.baseUrl}/circles/${circle.id}`)
      .pipe(share());
  }

  // cities
  public getCities = (params: SearchParams): Observable<CitySearchResults> => {
    const p = this.serializeParamsObj(params, new HttpParams());
    return this.http.get<any>(`${this.baseUrl}/cities`, { params: p })
      .pipe(map(this.deserializeAs(CitySearchResults)));
  }

  public getCity = (id: number): Observable<City> => {
    return this.http.get<any>(`${this.baseUrl}/cities/${id}`)
      .pipe(map(this.deserializeAs(City)));
  }

  public updateCity = (city: City): Observable<City> => {
    return this.http.put<any>(`${this.baseUrl}/cities/${city.id}`, this.serialize(city))
      .pipe(map(this.deserializeAs(City)));
  }

  public createCity = (city: City): Observable<City> => {
    return this.http.post<any>(`${this.baseUrl}/cities`, this.serialize(city))
      .pipe(map(this.deserializeAs(City)));
  }

  public deleteCity = (city: City): Observable<City> => {
    return this.http.delete<any>(`${this.baseUrl}/cities/${city.id}`)
      .pipe(share());
  }
}